import React from 'react';
import PropTypes from 'prop-types';

export const evidenceChoiceIntroTitle =
  'Supporting documents and additional forms for your disability claim';

export const evidenceChoiceIntroQuestion =
  'Are there any supporting documents or additional forms that you want us to review with your claim?';

const evidenceChoiceIntroDescription = (
  <p>Support your claim by providing documents from any of these sources.</p>
);

const receivingCareHeader = <h4>Records of receiving care</h4>;

const receivingCareList = (
  <ul>
    <li>Civilian physicians or caregivers</li>
    <li>Counseling facilities or health clinics</li>
    <li>Sexual trauma crisis centers or centers for domestic abuse</li>
  </ul>
);

const writtenTestimonialHeader = (
  <h4>Buddy or lay statements, also called written testimonials</h4>
);

const writtenTestimonialList = (
  <ul>
    <li>Family members or roommates</li>
    <li>Faculty members</li>
    <li>Fellow service members</li>
    <li>Chaplains or clergy members</li>
  </ul>
);

const civilPoliceReportHeader = <h4>Civilian police reports</h4>;

const civilPoliceReportList = (
  <ul>
    <li>Police headquarters</li>
    <li>Local precincts</li>
  </ul>
);

const personalAccAndOtherCorrespondenceHeader = (
  <h4>Personal accounts and other correspondence</h4>
);

const personalAccAndOtherCorrespondenceList = (
  <ul>
    <li>Personal journal or diary entries</li>
    <li>Emails, messages, or letters</li>
  </ul>
);

const additionalFormsHeader = <h4>Additional forms</h4>;

const additionalFormsList = (
  <ul>
    <li>Requests for increased compensation if you can’t work</li>
    <li>Requests for an automobile allowance</li>
    <li>Applications for an adaptive-equipment grant for your automobile</li>
  </ul>
);

const additionalFormsLearnMoreLink = (
  <va-link
    external
    href="https://www.va.gov/disability/how-to-file-claim/additional-forms/"
    text="Learn more about the additional forms you can submit"
  />
);

const additionalFormsLinkAndNote = (
  <p>
    {additionalFormsLearnMoreLink}
    <br />
    <br />
    <strong>Note: </strong> You only need to submit new evidence that we don’t
    already have.
  </p>
);

export const evidenceChoiceIntroDescriptionContent = (
  <>
    {evidenceChoiceIntroDescription}
    {receivingCareHeader}
    {receivingCareList}
    {writtenTestimonialHeader}
    {writtenTestimonialList}
    {civilPoliceReportHeader}
    {civilPoliceReportList}
    {personalAccAndOtherCorrespondenceHeader}
    {personalAccAndOtherCorrespondenceList}
    {additionalFormsHeader}
    {additionalFormsList}
    {additionalFormsLinkAndNote}
  </>
);

export const ConfirmationEvidenceChoiceIntro = ({ formData }) => {
  const hasOtherEvidence =
    formData?.['view:selectableEvidenceTypes']?.['view:hasOtherEvidence'];

  return (
    <li>
      <h4>{evidenceChoiceIntroTitle}</h4>
      <div className="vads-u-color--gray">{evidenceChoiceIntroQuestion}</div>
      <div>{hasOtherEvidence ? 'Yes' : 'No'}</div>
    </li>
  );
};

ConfirmationEvidenceChoiceIntro.propTypes = {
  formData: PropTypes.shape({
    'view:selectableEvidenceTypes': PropTypes.shape({
      'view:hasOtherEvidence': PropTypes.bool,
    }),
  }),
};

export const additionalEvidenceModalContent =
  'You can choose not to include supporting documents and additional forms. If you do so, we’ll delete these files you uploaded:';

export const missingSelectionErrorMessage = 'You must provide a response';

// Markup for the "B" experience of the A/B study for the conditions evidence messaging test (~Sep 2026)
export const evidenceChoiceIntroTitleAlt =
  'Supporting documents and additional forms';

export const evidenceChoiceIntroHintAlt =
  'If you’re not sure, review the examples of possible documents and forms to include. You only need to submit new documents and forms that we don’t already have.';

export const evidenceChoiceIntroAccordionContent = (
  <>
    <h4 className="vads-u-margin-top--2">
      Examples of supporting documents and additional forms
    </h4>
    {/* Omitting open-single surfaces va-accordion's built-in "Expand all" /
        "Collapse all" links, since there are multiple accordion items. */}
    <va-accordion sectionHeading="supporting documents and additional forms">
      <va-accordion-item
        bordered
        class="vads-u-margin-y--1"
        header="Buddy or lay statements"
        id="buddy-or-lay-statements"
        level="4"
      >
        <p>
          These documents are also called “written testimonials” and may come
          from these people:
        </p>
        <ul>
          <li>Fellow service members</li>
          <li>Family members or roommates</li>
          <li>A personal statement that you write</li>
          <li>Chaplains or clergy members</li>
          <li>Faculty members</li>
        </ul>
      </va-accordion-item>
      <va-accordion-item
        bordered
        class="vads-u-margin-y--1"
        header="Records from military service"
        id="records-from-military-service"
        level="4"
      >
        <ul>
          <li>Discharge or separation papers, such as DD214</li>
          <li>Service treatment records</li>
          <li>Military Personnel Record</li>
          <li>Photos or news clippings</li>
          <li>Ship logs</li>
        </ul>
      </va-accordion-item>
      <va-accordion-item
        bordered
        class="vads-u-margin-y--1"
        header="Records related to traumatic events"
        id="records-related-to-traumatic-events"
        level="4"
      >
        <ul>
          <li>Counseling facilities or health clinics</li>
          <li>Sexual trauma crisis centers or centers for domestic abuse</li>
          <li>Civilian police reports</li>
        </ul>
      </va-accordion-item>
      <va-accordion-item
        bordered
        class="vads-u-margin-y--1"
        header="Personal accounts or correspondence"
        id="personal-accounts-or-correspondence"
        level="4"
      >
        <ul>
          <li>Personal diaries or journals</li>
          <li>Emails, messages, or letters</li>
        </ul>
      </va-accordion-item>
      <va-accordion-item
        bordered
        class="vads-u-margin-y--1"
        header="Additional forms"
        id="additional-forms"
        level="4"
      >
        <p>
          If you’ve filled any additional forms, you can upload them to support
          your claim. These are some examples:
        </p>
        <ul>
          <li>
            Individual unemployability:{' '}
            <va-link
              external
              href="https://www.va.gov/forms/21-8940/"
              text="VA Form 21-8940"
            />
          </li>
          <li>
            Aid & attendance:{' '}
            <va-link
              external
              href="https://www.va.gov/forms/21-2680/"
              text="VA Form 21-2680"
            />
          </li>
          <li>
            Housing adaptation:{' '}
            <va-link
              external
              href="https://www.va.gov/forms/26-4555/"
              text="VA Form 26-4555"
            />
          </li>
          <li>
            Automobile adaptation:{' '}
            <va-link
              external
              href="https://www.va.gov/forms/21-4502/"
              text="VA Form 21-4502"
            />
          </li>
          <li>
            Add or remove dependents:{' '}
            <va-link
              external
              href="https://www.va.gov/forms/21-686c/"
              text="Form 21-686c"
            />
          </li>
        </ul>
        <p>{additionalFormsLearnMoreLink}</p>
      </va-accordion-item>
    </va-accordion>
  </>
);
// End of markup for the "B" experience of the A/B study
